import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Divider,
  Link,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { formatDistanceToNow } from "date-fns";

// Constants for length limits
const MAX_NAME_LENGTH = 30;
const MAX_INFO_LENGTH = 100;
const MAX_COMMENT_LENGTH = 1000;
const COMMENTS_PER_PAGE = 20;

function Comments({ comments = [], onSubmitComment }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [newComment, setNewComment] = useState("");
  const [userName, setUserName] = useState(
    localStorage.getItem("commentAuthor") || ""
  );
  const [userInfo, setUserInfo] = useState(
    localStorage.getItem("commentUserInfo") || ""
  );
  const commentRefs = useRef({});
  const [isExpanded, setIsExpanded] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollDirection, setScrollDirection] = useState("down"); // 'up' or 'down'
  const [visibleComments, setVisibleComments] = useState(COMMENTS_PER_PAGE);
  const commentsContainerRef = useRef(null);
  const formRef = useRef(null);

  // Sort comments by creation time
  const sortedComments = [...comments].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  useEffect(() => {
    const handleScroll = () => {
      if (commentsContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } =
          commentsContainerRef.current;
        setShowScrollTop(scrollTop > 200);

        // Set scroll direction based on position
        if (scrollTop < scrollHeight - clientHeight - 100) {
          setScrollDirection("down");
        } else {
          setScrollDirection("up");
        }

        // Load more comments when scrolling near bottom
        if (scrollHeight - scrollTop - clientHeight < 200) {
          if (visibleComments < sortedComments.length) {
            setVisibleComments((prev) =>
              Math.min(prev + COMMENTS_PER_PAGE, sortedComments.length)
            );
          }
        }
      }
    };

    const container = commentsContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [visibleComments, sortedComments.length]);

  const handleGlobalClick = (e) => {
    if (formRef.current && !formRef.current.contains(e.target)) {
      setIsExpanded(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleGlobalClick);
    return () => document.removeEventListener("mousedown", handleGlobalClick);
  }, []);

  const scrollToPosition = () => {
    const container = commentsContainerRef.current;
    if (container) {
      if (scrollDirection === "down") {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
        setScrollDirection("up");
      } else {
        container.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        setScrollDirection("down");
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newComment.trim() || !userName.trim()) return;

    // Save the user info for future comments
    localStorage.setItem("commentAuthor", userName);
    localStorage.setItem("commentUserInfo", userInfo);

    onSubmitComment({
      text: newComment,
      author: userName,
      userInfo: userInfo.trim() || null,
    });

    setNewComment("");
    setIsExpanded(false);
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  const handleBlur = (e) => {
    // Only collapse if clicking outside the form and no text is entered
    if (
      !e.currentTarget.contains(e.relatedTarget) &&
      !newComment &&
      !userName &&
      !userInfo
    ) {
      setIsExpanded(false);
    }
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value.slice(0, MAX_NAME_LENGTH);
    setUserName(value);
  };

  const handleInfoChange = (e) => {
    const value = e.target.value.slice(0, MAX_INFO_LENGTH);
    setUserInfo(value);
  };

  const handleCommentChange = (e) => {
    const value = e.target.value.slice(0, MAX_COMMENT_LENGTH);
    setNewComment(value);
  };

  const scrollToComment = (commentId) => {
    const element = commentRefs.current[commentId];
    if (element) {
      const container = commentsContainerRef.current;
      const elementTop = element.offsetTop;
      container.scrollTo({
        top: elementTop - 20,
        behavior: "smooth",
      });

      // Add a brief highlight effect
      element.style.backgroundColor = "rgba(144, 202, 249, 0.08)";
      setTimeout(() => {
        element.style.backgroundColor = "";
      }, 2000);
    }
  };

  const renderCommentText = (text) => {
    // Replace #X references with clickable links
    const parts = text.split(/(#\d+)/g);
    return parts.map((part, i) => {
      if (part.match(/^#\d+$/)) {
        const commentNum = parseInt(part.substring(1));
        if (commentNum <= sortedComments.length) {
          return (
            <Link
              key={i}
              component="button"
              onClick={() => scrollToComment(commentNum)}
              sx={{
                color: "primary.main",
                textDecoration: "none",
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {part}
            </Link>
          );
        }
      }
      return part;
    });
  };

  return (
    <Box sx={{ mt: 2, position: "relative" }}>
      {/* Comments List */}
      <Box
        ref={commentsContainerRef}
        sx={{
          position: "relative",
          maxHeight: "70vh",
          overflowY: "auto",
          overflowX: "hidden",
          scrollBehavior: "smooth",
          pr: 1.5,
          mr: -1.5,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(255, 255, 255, 0.05)",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "4px",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.15)",
            },
          },
        }}
      >
        {/* Scroll button */}
        {showScrollTop && (
          <IconButton
            onClick={scrollToPosition}
            sx={{
              position: "sticky",
              top: "calc(100% - 48px)", // Position at bottom while scrolling
              float: "right",
              right: 16,
              marginTop: "-48px", // Pull up to overlay content
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              backdropFilter: "blur(8px)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.4)",
              },
              zIndex: 2,
            }}
          >
            {scrollDirection === "down" ? (
              <KeyboardArrowDownIcon />
            ) : (
              <KeyboardArrowUpIcon />
            )}
          </IconButton>
        )}

        {sortedComments.slice(0, visibleComments).map((comment, index) => (
          <Box
            key={comment.id || index}
            ref={(el) => (commentRefs.current[index + 1] = el)}
            sx={{
              position: "relative",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.01)",
              },
              transition: "background-color 0.3s ease",
              cursor: "default",
              borderRadius: 1,
              px: 1.5,
              mx: -1.5,
            }}
          >
            <Box sx={{ mb: 1.5, pt: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  mb: 1,
                  gap: isMobile ? 0.5 : 1,
                  flexWrap: isMobile ? "wrap" : "nowrap",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    letterSpacing: "0.01em",
                    mr: "auto",
                  }}
                >
                  {comment.author}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    opacity: 0.7,
                    order: isMobile ? 3 : "initial",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  {comment.createdAt &&
                    formatDistanceToNow(new Date(comment.createdAt), {
                      addSuffix: true,
                    })}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    opacity: 0.4,
                    ml: 1,
                    userSelect: "none",
                    minWidth: "2em",
                    textAlign: "right",
                  }}
                >
                  #{index + 1}
                </Typography>
              </Box>
              {comment.userInfo && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 0.75,
                    color: "text.secondary",
                    opacity: 0.7,
                    wordBreak: "break-word",
                  }}
                >
                  {isValidUrl(comment.userInfo) ? (
                    <Link
                      href={comment.userInfo}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "primary.main",
                        opacity: 0.85,
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                          opacity: 1,
                        },
                      }}
                    >
                      {new URL(comment.userInfo).hostname}
                    </Link>
                  ) : (
                    comment.userInfo
                  )}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  color: "text.primary",
                  lineHeight: 1.5,
                  letterSpacing: "0.01em",
                  fontSize: "0.9375rem",
                  wordBreak: "break-word",
                }}
              >
                {renderCommentText(comment.text)}
              </Typography>
            </Box>
            {index < sortedComments.length - 1 && (
              <Divider
                sx={{
                  opacity: 0.3,
                  mb: 0,
                  mt: 0,
                }}
              />
            )}
          </Box>
        ))}
        {visibleComments < sortedComments.length && (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Button
              onClick={() =>
                setVisibleComments((prev) =>
                  Math.min(prev + COMMENTS_PER_PAGE, sortedComments.length)
                )
              }
              sx={{
                opacity: 0.7,
                "&:hover": {
                  opacity: 1,
                },
              }}
            >
              Show More Comments
            </Button>
          </Box>
        )}
      </Box>

      {/* Comment Input */}
      <Box
        component="form"
        ref={formRef}
        onSubmit={handleSubmit}
        onBlur={handleBlur}
        sx={{
          mt: 3,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          position: "relative",
          backgroundColor: "transparent",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            size="small"
            fullWidth
            required
            multiline
            minRows={isExpanded ? 2 : 1}
            maxRows={4}
            placeholder="Write a comment..."
            value={newComment}
            onChange={handleCommentChange}
            onFocus={() => setIsExpanded(true)}
            inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                transition: "all 0.2s ease",
                backgroundColor: "rgba(255, 255, 255, 0.02)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                },
                "&.Mui-focused": {
                  backgroundColor: "rgba(255, 255, 255, 0.04)",
                },
              },
            }}
          />
          <IconButton
            type="submit"
            color="primary"
            disabled={!newComment.trim() || !userName.trim()}
            sx={{
              alignSelf: "flex-start",
              mt: 0.5,
              opacity: isExpanded ? 1 : 0.7,
              transition: "all 0.2s ease",
              "&:hover": {
                opacity: 1,
              },
              "&.Mui-disabled": {
                opacity: 0.3,
              },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            maxHeight: isExpanded ? 40 : 0,
            opacity: isExpanded ? 1 : 0,
            overflow: "hidden",
            transition: "all 0.2s ease",
          }}
        >
          <TextField
            size="small"
            required
            placeholder="Your name"
            value={userName}
            onChange={handleNameChange}
            inputProps={{ maxLength: MAX_NAME_LENGTH }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                backgroundColor: "rgba(255, 255, 255, 0.02)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                },
                "&.Mui-focused": {
                  backgroundColor: "rgba(255, 255, 255, 0.04)",
                },
              },
            }}
          />
          <TextField
            size="small"
            placeholder={
              isMobile ? "Website/bio (optional)" : "Website or bio (optional)"
            }
            value={userInfo}
            onChange={handleInfoChange}
            inputProps={{ maxLength: MAX_INFO_LENGTH }}
            sx={{
              flex: isMobile ? 1 : 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                backgroundColor: "rgba(255, 255, 255, 0.02)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                },
                "&.Mui-focused": {
                  backgroundColor: "rgba(255, 255, 255, 0.04)",
                },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default Comments;
